/**
 * Zustand Store for Shared Spaces
 *
 * Manages shared spaces state including:
 * - List of spaces user has access to
 * - Current active space
 * - Space members and permissions
 * - CRUD operations for spaces
 *
 * Hybrid Storage Strategy:
 * - Firestore is the source of truth for all spaces
 * - IndexedDB is used as a cache layer for offline access
 */

import { create } from 'zustand';
import { db, spacesDB, type SharedSpace, type SpaceMember, type SpaceSettings } from '../../lib/db';
import * as spacesService from '../../services/spaces.service';
import * as membersService from '../../services/members.service';
import * as driveSharingService from '../../services/drive-sharing.service';
import type { Space, CreateSpaceInput, UpdateSpaceInput } from '../../types/firestore';
import type { SpaceFeatures } from '../../config/space-templates';

interface SpacesState {
  // State
  spaces: SharedSpace[];
  currentSpaceId: string;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadSpaces: () => Promise<void>;
  getCurrentSpace: () => SharedSpace | undefined;
  getPersonalSpaces: (userId: string) => SharedSpace[];
  getSharedSpaces: (userId: string) => SharedSpace[];
  setCurrentSpace: (spaceId: string) => void;
  createSpace: (
    name: string,
    type: string,
    description?: string,
    color?: string,
    userId?: string,
    customLabel?: string,
    icon?: string,
    features?: SpaceFeatures,
    googleCalendarSyncEnabled?: boolean
  ) => Promise<SharedSpace>;
  updateSpace: (spaceId: string, updates: Partial<SharedSpace>) => Promise<void>;
  deleteSpace: (spaceId: string) => Promise<void>;
  addMember: (spaceId: string, member: Omit<SpaceMember, 'joinedAt' | 'lastActive'>) => Promise<void>;
  removeMember: (spaceId: string, userId: string) => Promise<void>;
  updateMemberRole: (spaceId: string, userId: string, role: SpaceMember['role']) => Promise<void>;
  updateSettings: (spaceId: string, settings: Partial<SpaceSettings>) => Promise<void>;
}

/**
 * Helper: Convert Firebase Space to IndexedDB SharedSpace
 */
export function firestoreSpaceToSharedSpace(space: Space, members: any[]): SharedSpace {
  return {
    id: space.id,
    name: space.name,
    type: space.type,
    customLabel: space.customLabel,
    description: space.description || '',
    color: space.color,
    icon: space.icon,
    managerId: space.ownerId,
    members: members.map((m) => ({
      userId: m.userId,
      email: m.email,
      role: mapFirestoreRoleToIndexDB(m.role),
      joinedAt: m.joinedAt instanceof Date ? m.joinedAt.toISOString() : new Date().toISOString(),
      lastActive: m.lastActiveAt instanceof Date ? m.lastActiveAt.toISOString() : new Date().toISOString(),
    })),
    settings: {
      defaultCalendarView: (space.settings.defaultView || 'month') as 'month' | 'week' | 'day',
      allowGuests: true,
      sendNotifications: true,
      colorTheme: space.color || '#4A90E2',
    },
    features: space.features, // Include features from Firestore
    // Google Calendar integration fields
    googleCalendarId: space.googleCalendarId,
    googleCalendarSyncEnabled: space.googleCalendarSyncEnabled || false,
    googleCalendarColor: space.googleCalendarColor,
    syncStatus: 'synced' as const,
    createdAt: space.createdAt instanceof Date ? space.createdAt.toISOString() : new Date().toISOString(),
    updatedAt: space.updatedAt instanceof Date ? space.updatedAt.toISOString() : new Date().toISOString(),
  };
}

/**
 * Helper: Map Firestore role to IndexedDB role
 */
function mapFirestoreRoleToIndexDB(role: string): SpaceMember['role'] {
  switch (role) {
    case 'owner':
    case 'admin':
      return 'manager';
    case 'member':
      return 'editor';
    case 'viewer':
      return 'viewer';
    default:
      return 'viewer';
  }
}

export const useSpacesStore = create<SpacesState>((set, get) => ({
  // Initial state
  spaces: [],
  currentSpaceId: 'default',
  isLoading: false,
  error: null,

  // Load all spaces from Firestore (with IndexedDB cache)
  loadSpaces: async () => {
    set({ isLoading: true, error: null });
    try {
      // Try to load from Firestore first
      const firestoreSpaces = await spacesService.getUserSpaces();

      // Convert to SharedSpace format with minimal data (no members loaded - reduces queries)
      // Member list is only needed when viewing space details, not on dashboard
      const spaces = firestoreSpaces.map((space) => {
        return firestoreSpaceToSharedSpace(space, []); // Pass empty members array
      });

      // Update IndexedDB cache (use put to create or update)
      await Promise.all(
        spaces.map(async (space) => {
          const existing = await spacesDB.get(space.id);
          if (existing) {
            await spacesDB.update(space.id, space);
          } else {
            // Space doesn't exist in IndexedDB yet, add it
            await db.spaces.put(space);
          }
        })
      );

      set({ spaces, isLoading: false });
    } catch (error) {
      console.error('Failed to load from Firestore, falling back to IndexedDB:', error);

      // Fallback to IndexedDB if Firestore fails (offline mode)
      try {
        const cachedSpaces = await spacesDB.getAll();
        set({
          spaces: cachedSpaces,
          isLoading: false,
          error: 'Using cached data (offline mode)'
        });
      } catch (cacheError) {
        set({
          error: error instanceof Error ? error.message : 'Failed to load spaces',
          isLoading: false,
        });
      }
    }
  },

  // Get current space
  getCurrentSpace: () => {
    const { spaces, currentSpaceId } = get();
    return spaces.find((space) => space.id === currentSpaceId);
  },

  // Get personal spaces (where user is the manager/owner)
  getPersonalSpaces: (userId: string) => {
    const { spaces } = get();
    return spaces.filter((space) => space.managerId === userId);
  },

  // Get shared spaces (where user is a member but not the manager)
  getSharedSpaces: (userId: string) => {
    const { spaces } = get();
    return spaces.filter((space) =>
      space.managerId !== userId &&
      space.members.some((member) => member.userId === userId)
    );
  },

  // Set current space
  setCurrentSpace: (spaceId: string) => {
    set({ currentSpaceId: spaceId });
  },

  // Create a new space (Firestore + Google Drive + optional Google Calendar)
  createSpace: async (name, type, description, color, userId, customLabel, icon, features, googleCalendarSyncEnabled = false) => {
    set({ isLoading: true, error: null });
    try {
      if (!userId) {
        throw new Error('User ID is required to create a space');
      }

      // Validate unique name across all spaces (case-insensitive)
      const { spaces } = get();
      const duplicateName = spaces.find(
        (space) => space.name.toLowerCase() === name.toLowerCase()
      );
      if (duplicateName) {
        throw new Error(`A space with the name "${name}" already exists`);
      }

      // 1. Create Drive folder for the space (automatically gets auth token)
      let driveFolderId: string | undefined;
      try {
        const driveFolder = await driveSharingService.createSpaceFolder(name);
        driveFolderId = driveFolder.id;
        console.log(`[Spaces Store] Created Drive folder for space: ${driveFolderId}`);
      } catch (driveError) {
        console.error('[Spaces Store] Failed to create Drive folder:', driveError);
        // Don't fail space creation if Drive folder creation fails
        console.warn('Space will be created without Drive folder');
      }

      // 2. Create Google Calendar for the space (if enabled and Calendar API is available)
      let googleCalendarId: string | undefined;
      if (googleCalendarSyncEnabled) {
        try {
          // Import calendar client dynamically to avoid circular dependencies
          const { calendarClient } = await import('../../lib/google-calendar');

          const calendarResult = await calendarClient.createCalendar(
            name,
            description || `Shared calendar for ${name}`,
            undefined, // Use default timezone
            color || '#4A90E2'
          );

          googleCalendarId = calendarResult.id;
          console.log(`[Spaces Store] Created Google Calendar for space: ${googleCalendarId}`);
        } catch (calendarError) {
          console.error('[Spaces Store] Failed to create Google Calendar:', calendarError);
          // Don't fail space creation if calendar fails, just warn
          console.warn('Space will be created without calendar sync');
          googleCalendarSyncEnabled = false; // Disable sync if calendar creation failed
        }
      }

      // 3. Create space in Firestore
      const createSpaceInput: CreateSpaceInput = {
        name,
        type: type as any,
        customLabel,
        description,
        color: color || '#4A90E2',
        icon,
        googleCalendarSyncEnabled,
        settings: {
          allowMemberInvites: true,
          visibility: 'private',
        },
        features: features as any, // Cast to Firestore's SpaceFeatures type
      };

      const firestoreSpace = await spacesService.createSpace(
        createSpaceInput,
        driveFolderId
      );

      // 4. Update Firestore space with calendar ID if calendar was created
      if (googleCalendarId) {
        await spacesService.updateSpace(firestoreSpace.id, {
          googleCalendarColor: color || '#4A90E2',
        });
      }

      // 5. Get members to construct SharedSpace
      const members = await membersService.getSpaceMembers(firestoreSpace.id);
      const newSpace = firestoreSpaceToSharedSpace(firestoreSpace, members);

      // Add calendar ID to the SharedSpace object
      if (googleCalendarId) {
        newSpace.googleCalendarId = googleCalendarId;
        newSpace.googleCalendarColor = color || '#4A90E2';
      }

      // 6. Cache in IndexedDB
      await spacesDB.create(newSpace);

      set((state) => ({
        spaces: [...state.spaces, newSpace],
        isLoading: false,
      }));

      return newSpace;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create space',
        isLoading: false,
      });
      throw error;
    }
  },

  // Update a space (Firestore + IndexedDB)
  updateSpace: async (spaceId, updates) => {
    set({ isLoading: true, error: null });
    try {
      // Build Firestore update input
      const updateInput: UpdateSpaceInput = {};
      if (updates.name) updateInput.name = updates.name;
      if (updates.description !== undefined) updateInput.description = updates.description;
      if (updates.settings?.colorTheme) updateInput.color = updates.settings.colorTheme;
      if (updates.settings) {
        updateInput.settings = {
          allowMemberInvites: updates.settings.allowGuests ?? true,
          visibility: 'private',
          defaultView: updates.settings.defaultCalendarView === 'month' ? 'calendar' : 'notes',
        };
      }

      // Update in Firestore
      await spacesService.updateSpace(spaceId, updateInput);

      // Update in IndexedDB
      await spacesDB.update(spaceId, {
        ...updates,
        syncStatus: 'synced',
      });

      // Reload spaces
      const spaces = await spacesDB.getAll();
      set({ spaces, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update space',
        isLoading: false,
      });
      throw error;
    }
  },

  // Delete a space (soft delete in Firestore + delete calendar + delete Drive folder)
  deleteSpace: async (spaceId) => {
    set({ isLoading: true, error: null });
    try {
      // Get the space before deleting to check for calendar and Drive folder
      const space = await spacesDB.get(spaceId);

      // Delete the Google Calendar if it exists
      if (space?.googleCalendarId && space.googleCalendarSyncEnabled) {
        try {
          const { calendarClient } = await import('../../lib/google-calendar');
          await calendarClient.deleteCalendar(space.googleCalendarId);
          console.log(`[Spaces Store] Deleted Google Calendar: ${space.googleCalendarId}`);
        } catch (calendarError) {
          console.error('[Spaces Store] Failed to delete Google Calendar:', calendarError);
          // Don't fail space deletion if calendar deletion fails
          // The calendar will be orphaned but that's better than blocking space deletion
        }
      }

      // Delete the Drive folder if it exists
      if (space?.driveFolderId) {
        try {
          await driveSharingService.deleteSpaceFolder(space.driveFolderId);
          console.log(`[Spaces Store] Deleted Drive folder: ${space.driveFolderId}`);
        } catch (driveError) {
          console.error('[Spaces Store] Failed to delete Drive folder:', driveError);
          // Don't fail space deletion if Drive folder deletion fails
          // The folder will be orphaned but that's better than blocking space deletion
        }
      }

      // Soft delete in Firestore
      await spacesService.deleteSpace(spaceId);

      // Remove from IndexedDB
      await spacesDB.delete(spaceId);

      set((state) => ({
        spaces: state.spaces.filter((s) => s.id !== spaceId),
        // If deleting current space, switch to first available or default
        currentSpaceId:
          state.currentSpaceId === spaceId
            ? state.spaces.find((s) => s.id !== spaceId)?.id || 'default'
            : state.currentSpaceId,
        isLoading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete space',
        isLoading: false,
      });
      throw error;
    }
  },

  // Add a member to a space
  addMember: async (spaceId, member) => {
    try {
      const space = await spacesDB.get(spaceId);
      if (!space) throw new Error('Space not found');

      // Check if member already exists
      if (space.members.some((m) => m.userId === member.userId)) {
        throw new Error('Member already exists in this space');
      }

      const newMember: SpaceMember = {
        ...member,
        joinedAt: new Date().toISOString(),
        lastActive: new Date().toISOString(),
      };

      // Sync calendar ACL if space has calendar
      if (space.googleCalendarId && space.googleCalendarSyncEnabled) {
        try {
          const { addMemberToCalendar } = await import('../../lib/calendar-acl-sync');
          await addMemberToCalendar(space.googleCalendarId, newMember);
          console.log(`[Spaces Store] Added member to calendar ACL: ${member.email}`);
        } catch (calendarError) {
          console.error('[Spaces Store] Failed to add member to calendar ACL:', calendarError);
          // Don't fail member addition if calendar sync fails
        }
      }

      await spacesDB.update(spaceId, {
        members: [...space.members, newMember],
        syncStatus: 'pending',
      });

      // Reload spaces
      const spaces = await spacesDB.getAll();
      set({ spaces });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add member',
      });
      throw error;
    }
  },

  // Remove a member from a space
  removeMember: async (spaceId, userId) => {
    try {
      const space = await spacesDB.get(spaceId);
      if (!space) throw new Error('Space not found');

      // Can't remove the manager
      if (space.managerId === userId) {
        throw new Error('Cannot remove the space manager');
      }

      // Find the member being removed to get their email
      const memberToRemove = space.members.find((m) => m.userId === userId);

      // Revoke calendar access if space has calendar
      if (memberToRemove && space.googleCalendarId && space.googleCalendarSyncEnabled) {
        try {
          const { removeMemberFromCalendar } = await import('../../lib/calendar-acl-sync');
          await removeMemberFromCalendar(space.googleCalendarId, memberToRemove.email);
          console.log(`[Spaces Store] Removed member from calendar ACL: ${memberToRemove.email}`);
        } catch (calendarError) {
          console.error('[Spaces Store] Failed to remove member from calendar ACL:', calendarError);
          // Don't fail member removal if calendar sync fails
        }
      }

      await spacesDB.update(spaceId, {
        members: space.members.filter((m) => m.userId !== userId),
        syncStatus: 'pending',
      });

      // Reload spaces
      const spaces = await spacesDB.getAll();
      set({ spaces });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove member',
      });
      throw error;
    }
  },

  // Update a member's role
  updateMemberRole: async (spaceId, userId, role) => {
    try {
      const space = await spacesDB.get(spaceId);
      if (!space) throw new Error('Space not found');

      // Find the member being updated
      const memberToUpdate = space.members.find((m) => m.userId === userId);

      // Update calendar ACL if space has calendar
      if (memberToUpdate && space.googleCalendarId && space.googleCalendarSyncEnabled) {
        try {
          const { updateMemberCalendarRole } = await import('../../lib/calendar-acl-sync');
          await updateMemberCalendarRole(space.googleCalendarId, memberToUpdate.email, role);
          console.log(`[Spaces Store] Updated member calendar role: ${memberToUpdate.email} to ${role}`);
        } catch (calendarError) {
          console.error('[Spaces Store] Failed to update member calendar role:', calendarError);
          // Don't fail role update if calendar sync fails
        }
      }

      const updatedMembers = space.members.map((m) =>
        m.userId === userId ? { ...m, role } : m
      );

      await spacesDB.update(spaceId, {
        members: updatedMembers,
        syncStatus: 'pending',
      });

      // Reload spaces
      const spaces = await spacesDB.getAll();
      set({ spaces });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update member role',
      });
      throw error;
    }
  },

  // Update space settings
  updateSettings: async (spaceId, settings) => {
    try {
      const space = await spacesDB.get(spaceId);
      if (!space) throw new Error('Space not found');

      await spacesDB.update(spaceId, {
        settings: { ...space.settings, ...settings },
        syncStatus: 'pending',
      });

      // Reload spaces
      const spaces = await spacesDB.getAll();
      set({ spaces });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update settings',
      });
      throw error;
    }
  },
}));
